import { useEffect, useRef, useState } from "react";
import { Download, Upload, Loader2, AlertTriangle, CheckCircle2, History, ArrowRightLeft, Info } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const when = (s) => (s ? new Date(s).toLocaleString() : "—");
const keyOf = (k) => Object.values(k || {})[0] || "";

/** Permissions → Sync: export this environment's access config, or merge a
 *  file exported elsewhere into this one. Merge never wipes — it adds what is
 *  missing and replays the journal so removals travel too. */
export default function AccessSyncTab() {
  const fileRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [fileName, setFileName] = useState("");
  const [plan, setPlan] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [replay, setReplay] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [run, setRun] = useState(null);

  const loadStatus = () => api.get("/access-sync/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  useEffect(() => { loadStatus(); }, []);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/access-sync/export`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") || "")?.[1] || "conduit-access-config.json";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = name; a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported ${name}`);
    } catch (e) { toast.error(`Export failed: ${e.message}`); }
    finally { setExporting(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setRun(null); setPlan(null); setBundle(null);
    let parsed;
    try { parsed = JSON.parse(await f.text()); }
    catch { toast.error("That file isn't valid JSON"); return; }
    setBundle(parsed); setFileName(f.name);
    setInspecting(true);
    try {
      const { data } = await api.post("/access-sync/inspect", { bundle: parsed });
      setPlan(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't read that export");
      setBundle(null);
    } finally { setInspecting(false); }
  };

  const doApply = async () => {
    setConfirm(false); setApplying(true);
    try {
      const { data } = await api.post("/access-sync/apply", { bundle, replay_journal: replay });
      setRun(data); setPlan(null); setBundle(null);
      toast.success("Access config merged");
      loadStatus();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Merge failed");
    } finally { setApplying(false); }
  };

  const c = status?.counts || {};
  return (
    <div className="space-y-4" data-testid="access-sync-tab">
      <div className="flex flex-wrap items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <div>
          <span className="font-medium text-slate-800">Carry access rules between preview and production.</span>{" "}
          Export here, deploy, then import the file on the other side. The import is a <b>merge</b>: missing apps, audiences and roles are added,
          permission lists are combined, and the change journal replays removals and renames — nothing that only exists on the other side is deleted.
          Users, organizations and their per-person grants never travel.
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-slate-200 p-4 shadow-sm" data-testid="access-sync-export-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-heading text-sm font-semibold text-slate-800">Export this environment</h3>
              <p className="mt-1 text-xs text-slate-500">
                <span className="font-mono text-slate-600" data-testid="access-sync-env">{status?.env || "…"}</span> ·{" "}
                {c.audiences ?? "–"} audiences · {c.roles ?? "–"} roles · {c.apps ?? "–"} apps · {c.journal ?? "–"} journal entries
              </p>
            </div>
            <Button onClick={doExport} disabled={exporting} data-testid="access-sync-export-button" className="bg-primary text-primary-foreground hover:bg-primary/90">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download access config
            </Button>
          </div>
        </Card>

        <Card className="border-slate-200 p-4 shadow-sm" data-testid="access-sync-import-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-heading text-sm font-semibold text-slate-800">Import into this environment</h3>
              <p className="mt-1 text-xs text-slate-500">Pick an access config file exported elsewhere. You get a full preview before anything is written.</p>
            </div>
            <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={onFile} data-testid="access-sync-file-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={inspecting} data-testid="access-sync-choose-file">
              {inspecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Choose file
            </Button>
          </div>
          {fileName && <p className="mt-2 text-xs text-slate-500" data-testid="access-sync-file-name">{fileName}</p>}
        </Card>
      </div>

      {plan && <MergePreview plan={plan} replay={replay} setReplay={setReplay} onApply={() => setConfirm(true)} applying={applying} />}
      {run && <RunReport run={run} />}

      <RecentJournal status={status} />

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merge access config into {status?.env}?</AlertDialogTitle>
            <AlertDialogDescription>
              From <b>{plan?.source?.env}</b> (exported {when(plan?.source?.exported_at)}). Adds {plan?.apps_new?.length || 0} apps,{" "}
              {plan?.audiences_new?.length || 0} audiences and {plan?.roles_new?.length || 0} roles, updates {plan?.audiences_changed?.length || 0} audiences and{" "}
              {plan?.roles_changed?.length || 0} roles{replay ? `, and replays ${plan?.journal_replay?.length || 0} journal entries` : ""}. Existing users keep their own grants.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doApply} data-testid="access-sync-confirm-apply" className="bg-primary hover:bg-primary/90">Merge now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Section({ title, count, children, testid }) {
  if (!count) return null;
  return (
    <div data-testid={testid}>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title} · {count}</div>
      <div className="space-y-1 text-xs text-slate-700">{children}</div>
    </div>
  );
}

function MergePreview({ plan, replay, setReplay, onApply, applying }) {
  const sameEnv = plan.source?.env && plan.source.env === plan.target_env;
  return (
    <Card className="border-primary/30 p-4 shadow-sm" data-testid="access-sync-preview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-800">
            <ArrowRightLeft className="h-4 w-4 text-primary" /> Preview: {plan.source?.env || "unknown"} → {plan.target_env}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">Exported {when(plan.source?.exported_at)} by {plan.source?.exported_by || "—"}. Nothing has been written yet.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <Checkbox checked={replay} onCheckedChange={() => setReplay((v) => !v)} data-testid="access-sync-replay-toggle" /> Replay journal (removals, renames, deletes)
          </label>
          <Button onClick={onApply} disabled={applying || plan.nothing_to_do || sameEnv} data-testid="access-sync-apply-button" className="bg-primary text-primary-foreground hover:bg-primary/90">
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Apply merge
          </Button>
        </div>
      </div>
      {sameEnv && <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700" data-testid="access-sync-same-env"><AlertTriangle className="h-3.5 w-3.5" /> This file was exported from this same environment — there is nothing to merge.</p>}
      {plan.nothing_to_do && !sameEnv && <p className="mt-2 text-xs text-emerald-700" data-testid="access-sync-nothing">Everything in that export is already here.</p>}
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <Section title="Apps to add" count={plan.apps_new?.length} testid="access-sync-apps-new">
          {plan.apps_new.map((a) => <div key={a.slug}>{a.name} <span className="font-mono text-slate-400">{a.slug} · {a.permission_module}.view</span></div>)}
        </Section>
        <Section title="Apps getting a permission module" count={plan.apps_module?.length} testid="access-sync-apps-module">
          {plan.apps_module.map((a) => <div key={a.slug}>{a.name} → <span className="font-mono">{a.permission_module}</span></div>)}
        </Section>
        <Section title="Audiences to add" count={plan.audiences_new?.length} testid="access-sync-aud-new">
          {plan.audiences_new.map((a) => <div key={a.audience_id}>{a.label} <span className="text-slate-400">· {a.permissions.length} defaults</span></div>)}
        </Section>
        <Section title="Audiences to update" count={plan.audiences_changed?.length} testid="access-sync-aud-changed">
          {plan.audiences_changed.map((a) => (
            <div key={a.audience_id}>{a.label}: {a.add_permissions.length ? <>+{a.add_permissions.length} defaults <span className="font-mono text-slate-400">({a.add_permissions.slice(0, 6).join(", ")}{a.add_permissions.length > 6 ? ", …" : ""})</span></> : null}
              {a.scalars_differ.length ? <span className="text-slate-400"> · {a.scalars_differ.join(", ")} differ (journal decides)</span> : null}</div>
          ))}
        </Section>
        <Section title="Roles to add" count={plan.roles_new?.length} testid="access-sync-roles-new">
          {plan.roles_new.map((r) => <div key={r.role_id}>{r.name} <span className="text-slate-400">· {r.base_role} · +{r.permissions.length} / −{r.revoked.length}</span></div>)}
        </Section>
        <Section title="Roles to update" count={plan.roles_changed?.length} testid="access-sync-roles-changed">
          {plan.roles_changed.map((r) => (
            <div key={r.role_id}>{r.name}: {r.add_permissions.length ? `+${r.add_permissions.length} grants` : ""}{r.add_permissions.length && r.add_revoked.length ? ", " : ""}{r.add_revoked.length ? `+${r.add_revoked.length} revokes` : ""}
              <span className="font-mono text-slate-400"> ({[...r.add_permissions, ...r.add_revoked].slice(0, 6).join(", ")}{r.add_permissions.length + r.add_revoked.length > 6 ? ", …" : ""})</span>
              {r.scalars_differ.length ? <span className="text-slate-400"> · {r.scalars_differ.join(", ")} differ (journal decides)</span> : null}</div>
          ))}
        </Section>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Section title="Journal entries to replay" count={plan.journal_replay?.length} testid="access-sync-journal-replay">
          <JournalRows rows={plan.journal_replay} />
        </Section>
        <Section title="Journal entries skipped (snapshot wins)" count={plan.journal_skipped?.length} testid="access-sync-journal-skipped">
          <JournalRows rows={plan.journal_skipped} />
        </Section>
      </div>
      <p className="mt-3 text-[11px] text-slate-400" data-testid="access-sync-journal-counts">
        {plan.journal_already_applied} journal entries already applied here · {plan.journal_local} originated here.
      </p>
      {plan.dropped_permissions?.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="access-sync-dropped">
          <div className="mb-1 flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> {plan.dropped_permissions.length} permissions won&apos;t be imported — their module doesn&apos;t exist here</div>
          <div className="font-mono">{plan.dropped_permissions.slice(0, 12).join(" · ")}{plan.dropped_permissions.length > 12 ? " · …" : ""}</div>
        </div>
      )}
    </Card>
  );
}

function JournalRows({ rows }) {
  return (
    <div className="max-h-56 space-y-1 overflow-auto rounded-md border border-slate-100 p-2">
      {rows.map((e) => (
        <div key={e.entry_id} className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`journal-entry-${e.entry_id}`}>
          <span className={`rounded px-1.5 py-0.5 font-semibold uppercase ${e.op === "delete" ? "bg-rose-100 text-rose-700" : e.op === "pull" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{e.op}</span>
          <span className="text-slate-500">{e.coll}</span>
          <span className="font-medium text-slate-700">{e.label || keyOf(e.key)}</span>
          {e.field && <span className="font-mono text-slate-500">{e.field}{e.value != null ? ` = ${typeof e.value === "string" ? e.value : JSON.stringify(e.value)}` : ""}</span>}
          <span className="ml-auto text-slate-400">{e.actor} · {when(e.at)}</span>
          {e.reason && <span className="text-amber-700">({e.reason})</span>}
        </div>
      ))}
    </div>
  );
}

function RunReport({ run }) {
  const s = run.summary || {};
  return (
    <Card className="border-emerald-200 bg-emerald-50/40 p-4 shadow-sm" data-testid="access-sync-run">
      <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-emerald-800"><CheckCircle2 className="h-4 w-4" /> Merged from {run.source_env} · {when(run.at)}</h3>
      <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-700" data-testid="access-sync-run-summary">
        <span>+{s.apps_inserted} apps</span><span>{s.apps_module_set} modules set</span>
        <span>+{s.audiences_inserted} / ↑{s.audiences_updated} audiences</span>
        <span>+{s.roles_inserted} / ↑{s.roles_updated} roles</span>
        <span>{s.journal_applied} journal replayed · {s.journal_skipped} skipped</span>
      </div>
      {run.journal_outcomes?.length > 0 && (
        <div className="mt-2 max-h-40 overflow-auto text-[11px] text-slate-600">
          {run.journal_outcomes.map((o) => <div key={o.entry_id}>{o.op} {o.label} {o.field ? `${o.field}=${JSON.stringify(o.value)}` : ""} → <span className="font-medium">{o.outcome}</span></div>)}
        </div>
      )}
    </Card>
  );
}

function RecentJournal({ status }) {
  const rows = status?.journal || [];
  return (
    <Card className="border-slate-200 p-4 shadow-sm" data-testid="access-sync-journal-card">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-800"><History className="h-4 w-4 text-slate-400" /> Change journal</h3>
        <span className="text-xs text-slate-400">{status?.counts?.journal ?? 0} entries · removals, renames and deletes made here, so an import elsewhere can repeat them</span>
      </div>
      {status?.runs?.length > 0 && (
        <div className="mt-2 text-[11px] text-slate-500" data-testid="access-sync-runs">
          Last imports: {status.runs.slice(0, 3).map((r) => `${when(r.at)} from ${r.source_env} by ${r.actor}`).join(" · ")}
        </div>
      )}
      <div className="mt-3">
        {rows.length === 0 ? <div className="py-4 text-center text-xs text-slate-400" data-testid="access-sync-journal-empty">No removals recorded yet.</div> : <JournalRows rows={rows} />}
      </div>
    </Card>
  );
}
